import { Component, effect, inject, input, signal } from '@angular/core';
import { map } from 'rxjs/operators';
import { Utils } from 'src/app/utils.class';
import { AfasService, Attachment, InvoicePayload } from './../../services/afas.service';

@Component({
    selector: 'app-project-invoices',
    standalone: false,
    templateUrl: './project-invoices.component.html',
    styleUrl: './project-invoices.component.scss',
})
export class ProjectInvoicesComponent {
    readonly maxDownload = 50;

    projectID = input<string>();

    afasService = inject(AfasService);

    invoices = signal<InvoicePayload[]>([]);
    loading = signal<boolean>(false);
    downloading = signal<boolean>(false);

    selectedItems = signal<Item[]>([]);
    attachmentNumbers = signal<Map<string, number>>(new Map());
    displayRows = signal<{ attachment: Attachment; invoice: InvoicePayload }[]>([]);

    private selectionRangeStart = signal<number>(0);

    constructor() {
        // When projectID changes, fetch new invoices
        effect(() => {
            const id = this.projectID();
            if (id) {
                this.fetchInvoices(id);
            }
        });
    }

    splitFilename(filename: string): { base: string; ext: string } {
        const dotIndex = filename.lastIndexOf('.');
        if (dotIndex <= 0) {
            return { base: filename, ext: '' };
        }
        return { base: filename.slice(0, dotIndex), ext: filename.slice(dotIndex) };
    }

    private buildInvoiceFilename(invoice: InvoicePayload): string {
        const sanitizeFileName = (value: string) => value.replace(/\s+/g, '_').replace(/\./g, '');

        const reference = invoice.inkoopfactuur || 'onbekend';
        const date = Utils.formatDate(invoice.datum, 'ddMMMyyyy');
        const description = sanitizeFileName(invoice.omschrijving || 'onbekend');

        return `${description}_${reference}_${date}.pdf`;
    }

    fetchInvoices(projectId: string) {
        this.loading.set(true);

        this.afasService
            .getInvoicesByProject(projectId)
            .pipe(
                map(response =>
                    response.map(invoice => ({
                        ...invoice,
                        filename: this.buildInvoiceFilename(invoice),
                    }))
                )
            )
            .subscribe({
                next: response => {
                    this.invoices.set(response || []);
                    this.displayRows.set(this.buildDisplayRows(response || []));

                    const allItems = this.buildAllItems(response || [], projectId);
                    this.updateAttachmentNumbers(allItems);

                    // Select the first batch (up to the download limit) by default
                    this.selectionRangeStart.set(0);
                    this.selectedItems.set(allItems.slice(0, this.maxDownload));

                    this.loading.set(false);
                },
                error: err => {
                    this.loading.set(false);
                },
            });
    }

    private buildDisplayRows(invoices: InvoicePayload[]): { attachment: Attachment; invoice: InvoicePayload }[] {
        const rows: { attachment: Attachment; invoice: InvoicePayload }[] = [];
        invoices.forEach(invoice => {
            invoice.subject?.attachments?.forEach(attachment => {
                rows.push({ attachment, invoice });
            });
        });
        return rows;
    }

    private buildAllItems(invoices: InvoicePayload[], projectId: string): Item[] {
        const items: Item[] = [];
        invoices.forEach(invoice => {
            invoice.subject?.attachments?.forEach(attachment => {
                items.push(this.buildItem(attachment, invoice, projectId || 'unknown'));
            });
        });
        return items;
    }

    private updateAttachmentNumbers(items: Item[]) {
        const numbers = new Map<string, number>();
        items.forEach((item, index) => numbers.set(this.itemKey(item), index + 1));
        this.attachmentNumbers.set(numbers);
    }

    private itemKey(item: { subjectid: number; fileid: string }): string {
        return `${item.subjectid}_${item.fileid}`;
    }

    getAttachmentNumber(attachment: Attachment): number | undefined {
        return this.attachmentNumbers().get(this.itemKey(attachment));
    }

    toggleSelection(attachment: Attachment, invoice: InvoicePayload) {
        const current = this.selectedItems();
        const index = current.findIndex(
            item => item.subjectid === attachment.subjectid && item.fileid === attachment.fileid
        );

        if (index > -1) {
            // Remove from selection
            this.selectedItems.set([...current.slice(0, index), ...current.slice(index + 1)]);
        } else {
            // Add to selection
            const projectId = this.projectID() || 'unknown';
            this.selectedItems.set([...current, this.buildItem(attachment, invoice, projectId)]);
        }
    }

    isSelected(attachment: Attachment): boolean {
        return this.selectedItems().some(
            item => item.subjectid === attachment.subjectid && item.fileid === attachment.fileid
        );
    }

    getTotalAttachments(): number {
        let total = 0;
        this.invoices().forEach(invoice => {
            if (invoice.subject?.attachments) {
                total += invoice.subject.attachments.length;
            }
        });
        return total;
    }

    isAllSelected(): boolean {
        const total = this.getTotalAttachments();
        return total > 0 && this.selectedItems().length === total;
    }

    toggleSelectAll() {
        if (this.isAllSelected()) {
            this.selectedItems.set([]);
        } else {
            const projectId = this.projectID() || 'unknown';
            this.selectedItems.set(this.buildAllItems(this.invoices(), projectId));
        }
    }

    selectFirstBatch() {
        const allItems = this.buildAllItems(this.invoices(), this.projectID() || 'unknown');
        this.selectionRangeStart.set(0);
        this.selectedItems.set(allItems.slice(0, this.maxDownload));
    }

    selectNextBatch() {
        const allItems = this.buildAllItems(this.invoices(), this.projectID() || 'unknown');
        const nextStart = this.selectionRangeStart() + this.maxDownload;
        if (nextStart >= allItems.length) {
            return;
        }
        this.selectionRangeStart.set(nextStart);
        this.selectedItems.set(allItems.slice(nextStart, nextStart + this.maxDownload));
    }

    hasNextBatch(): boolean {
        return this.selectionRangeStart() + this.maxDownload < this.getTotalAttachments();
    }

    isOverLimit(): boolean {
        return this.selectedItems().length > this.maxDownload;
    }

    private buildItem(attachment: Attachment, invoice: InvoicePayload, projectId: string): Item {
        return {
            subjectid: attachment.subjectid,
            fileid: attachment.fileid,
            projectId: projectId || 'unknown',
            creditorname: invoice.omschrijving || 'onbekend',
            invoicenr: invoice.inkoopfactuur || 'onbekend',
            invoicedate: invoice.datum ? new Date(invoice.datum).toLocaleDateString() : 'onbekend',
            filename: invoice.filename ?? attachment.filename,
        };
    }

    private getSelectedNumberRange(): string {
        const numbers = this.selectedItems()
            .map(item => this.attachmentNumbers().get(this.itemKey(item)))
            .filter((n): n is number => n !== undefined)
            .sort((a, b) => a - b);

        if (numbers.length === 0) {
            return '';
        }

        // Group the exact selected numbers (the same ones shown in the list) into contiguous ranges
        const ranges: string[] = [];
        let start = numbers[0];
        let end = numbers[0];

        for (let i = 1; i <= numbers.length; i++) {
            const current = numbers[i];
            if (current === end + 1) {
                end = current;
                continue;
            }
            ranges.push(start === end ? `${start}` : `${start}-${end}`);
            start = current;
            end = current;
        }

        return ranges.join('_');
    }

    downloadSelected() {
        if (this.selectedItems().length === 0 || this.isOverLimit()) {
            return;
        }

        const payload = {
            items: this.selectedItems(),
        };
        this.downloading.set(true);

        const projectId = this.projectID() || 'unknown';
        const range = this.getSelectedNumberRange();
        const filename = `afas-inkoop_${projectId}_${range}_${Utils.formatDate(new Date(), 'dd-MMM-yyyy').replace('.', '')}.zip`;

        this.afasService
            .downloadZip(payload)
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                this.downloading.set(false);
            })
            .catch(err => {
                console.error('Download failed:', err);
                this.downloading.set(false);
            });
    }
}

interface Item {
    subjectid: number;
    fileid: string;
    projectId: string;
    creditorname: string;
    invoicenr: string;
    invoicedate: string;
    filename: string;
}
